import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { useQueryClient } from 'react-query';
import { CallRouteV1 } from '@frontend/api-call-route';
import { DepartmentsApi, DepartmentsTypes, DepartmentsUtils } from '@frontend/api-departments';
import { LocationsApi } from '@frontend/api-locations';
import { ScheduleApi } from '@frontend/api-schedule';
import { Page } from '@frontend/components';
import { useTranslation, Trans } from '@frontend/i18n';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useMutation } from '@frontend/react-query-helpers';
import { useAppScopeStore, useScopedQuery } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Button, ChipVariants, Table, Text, TextLink, useAlert } from '@frontend/design-system';
import { queryKeys } from '../query-keys';
import { logError } from '../utils/loggers';

const getStatusChipColor = (status: DepartmentsTypes.DepartmentScheduleTypes): ChipVariants => {
  switch (status) {
    case DepartmentsTypes.DepartmentScheduleTypes.Open:
      return 'success';
    case DepartmentsTypes.DepartmentScheduleTypes.Closed:
      return 'critical';
    case DepartmentsTypes.DepartmentScheduleTypes.Break:
      return 'warn';
    default:
      return 'primary';
  }
};

export const Departments = ({ isNwx }: { isNwx?: boolean }) => {
  const { t } = useTranslation('phone', { keyPrefix: 'departments' });
  const { error } = useAlert();
  const navigate = useNavigate();
  const { navigate: settingsNavigate } = useSettingsNavigate();
  const queryClient = useQueryClient();
  const { selectedLocationIds } = useAppScopeStore();
  const singleLocationId = selectedLocationIds[0];

  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');

  const [isLimitShown, setIsLimitShown] = useState(false);

  const { data: timezone, isLoading: isTimezoneLoading } = useScopedQuery({
    queryKey: queryKeys.timezone(singleLocationId),
    queryFn: async () => LocationsApi.getOfficeTimezone({}),
    select: (data) => data?.timezone,
    onError: () => {
      error(t('Error in retrieving timezone'));
      logError('Departments:timezoneQuery | Failed to fetch timezone', {
        data: { locationId: singleLocationId },
      });
    },
  });

  const listDepartmentsKey = [singleLocationId, queryKeys.listDepartments()];
  const { mutate: createDepartment, isLoading: creatingDepartment } = useMutation(
    () => {
      return DepartmentsApi.create({ name: 'Untitled Department' });
    },
    {
      onSettled: () => {
        queryClient.invalidateQueries(listDepartmentsKey);
      },
      onSuccess: async (data: DepartmentsTypes.CreateDepartmentTypes['output']) => {
        await refetch();
        //todo: navigate to the newly created department settings page
        settingsNavigate({ to: '/phone/departments/:id', params: { id: data.departmentId ?? '' } });
      },
      onError: (err) => {
        error(t(`unable to create department: ${err}`));
        logError('Departments:createDepartmentMutation | Failed to create department', {
          data: { locationId: singleLocationId, error: err },
        });
      },
    }
  );

  const { data: callRoutes = [] } = CallRouteV1.Queries.useListQuery({
    request: { tenantId: settingsTenantLocation?.phoneTenantId ?? '' },
    options: {
      enabled: !!settingsTenantLocation?.phoneTenantId,
      select: ({ callRoutes }) => callRoutes,
      meta: {
        error: {
          message: 'Departments:listCallRoutesQuery | Failed to fetch call routes',
          data: { tenantId: settingsTenantLocation?.phoneTenantId },
        },
      },
    },
  });

  const {
    data: departments,
    refetch,
    isLoading: isDepartmentsLoading,
  } = useScopedQuery({
    queryKey: queryKeys.listDepartmentsWithSchedules(),
    queryFn: async () => {
      const departments = await DepartmentsApi.listDept({ fwdEnabled: true, vmoEnabled: true });

      return Promise.all(
        (departments.departments ?? []).map((department: DepartmentsTypes.DepartmentModel) =>
          ScheduleApi.listSchedulesByDepartment({
            departmentId: department.id,
          }).then<DepartmentsTypes.DepartmentWithSchedules>((schedulesResponse: any) => ({
            ...department,
            schedules: schedulesResponse.schedules,
          }))
        )
      );
    },
    select: (data) => {
      return data?.sort((a, b) => (!a.name || !b.name ? 0 : a.name.localeCompare(b.name)));
    },
    onError: () => {
      error(t('Error in retrieving departments list'));
      logError('Departments:listDepartmentsWithSchedulesQuery | Failed to fetch departments list', {
        data: { locationId: singleLocationId },
      });
    },
  });

  const augmentedDepartments = useMemo(() => {
    if (!timezone || !departments) return [];
    return departments?.map((dep) => ({
      ...dep,
      status: DepartmentsUtils.getDepartmentScheduleStatus(dep, timezone),
      routingSummary: DepartmentsUtils.getDepartmentRoutingSummary(dep, timezone),
      callRouteName: callRoutes.find((route) => route.callRouteId === dep.callRouteId)?.name ?? '',
    }));
  }, [departments, timezone, callRoutes]);

  const getStatus = (status: DepartmentsTypes.DepartmentScheduleTypes) => {
    if (status === 'VMO' || status === 'FWD') {
      return t('Override');
    } else {
      return status;
    }
  };

  const isDepartmentLimitReached = !!departments && departments?.length > 11;

  return (
    <Page
      action={
        <>
          {isDepartmentLimitReached ? (
            <Button
              css={css`
                max-width: ${theme.spacing(19)};
              `}
              disabled
              onMouseEnter={() => {
                setIsLimitShown(true);
              }}
              onMouseLeave={() => {
                setIsLimitShown(false);
              }}
            >
              {isLimitShown && (
                <Text
                  color='light'
                  size='small'
                  css={css`
                    position: absolute;
                    bottom: ${theme.spacing(4)};
                    right: ${theme.spacing(1)};
                    width: ${theme.spacing(25)};
                    text-transform: none;
                  `}
                >
                  Max number of departments added
                </Text>
              )}
              Add Department
            </Button>
          ) : (
            <Button
              css={css`
                max-width: ${theme.spacing(19)};
              `}
              onClick={() => createDepartment()}
              disabled={creatingDepartment}
            >
              Add Department
            </Button>
          )}
        </>
      }
    >
      <Table
        emptyStateConfig={{
          type: 'sync_your_phone',
          header: t('No Departments'),
          description: () => (
            <>
              <Text as='span'>{t("You don't have any departments yet.")} </Text>
              <Trans t={t}>
                Select{' '}
                <Text as='span' weight='bold'>
                  Add Department
                </Text>{' '}
                to get started.
              </Trans>
            </>
          ),
          action: {
            label: t('Add Department'),
            onClick: () => createDepartment(),
            disabled: isDepartmentLimitReached,
          },
        }}
        hasGlobalSearch
        colConfig={[
          {
            Header: t('Name'),
            accessor: (department: DepartmentsTypes.DepartmentsWithSummary) => department.name,
            id: 'name',
            minWidth: 280,
            sortType: 'caseInsensitive',
          },
          {
            Header: t('Status'),
            id: 'status',
            accessor: (department) => getStatus(department.status),
            cellConfig: {
              element: 'Chip',
              // Added this 'any' type due to erroneous DS implicit 'any' type error
              variant: (cellValue: any) => getStatusChipColor(cellValue),
            },
          },
          {
            Header: t('Active Routing'),
            id: 'activeRouting',
            accessor: (department) => department,
            cellRenderer: (value: (typeof augmentedDepartments)[number]) => {
              return value.callRouteName ? (
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: ${theme.spacing(0.5)};
                  `}
                >
                  <TextLink
                    onClick={() =>
                      settingsNavigate({
                        to: '/phone/call-routes/:id',
                        params: { id: value.callRouteId ?? '' },
                      })
                    }
                  >
                    {value.callRouteName}
                  </TextLink>{' '}
                  <Text size='medium'>{t('(Call Route)')}</Text>
                </div>
              ) : (
                <Text size='medium'>{value.routingSummary}</Text>
              );
            },
          },
        ]}
        isLoading={isDepartmentsLoading || isTimezoneLoading}
        hasResponsiveColWidths
        data={augmentedDepartments ?? []}
        rowActions={{
          onRowClick: (data: DepartmentsTypes.DepartmentsWithSummary) => {
            if (isNwx) {
              settingsNavigate({ to: '/phone/departments/:id', params: { id: data.id ?? '' } });
            } else {
              navigate({ to: `./${data.id}` });
            }
          },
        }}
      ></Table>
    </Page>
  );
};
